import React, { useMemo, useState } from 'react';
import {
  Box, Button, Card, CardActionArea, Chip, IconButton, InputAdornment, ListItemIcon, ListItemText, Menu, MenuItem,
  Paper, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import RiceBowlOutlinedIcon from '@mui/icons-material/RiceBowlOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ListAltIcon from '@mui/icons-material/ListAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { CardGrid, EmptyState, Loader, NeedsHousehold, PageHeader, useLoad } from '../common';
import { frequencyText, searchKey, tagText } from '../labels';


/** "Habitual · 20 min · 5 ingredientes": quick and leftovers show as chips on the card. */
function cardSubtitle(r: T.RecipeSummary): string {
  const parts = [frequencyText(r.frequency)];
  if (r.course === 'Starter') parts.unshift('Entrada');
  if (r.course === 'Dessert') parts.unshift('Sobremesa');
  if (r.prepMinutes !== null && r.prepMinutes !== undefined) parts.push(`${r.prepMinutes} min`);
  parts.push(r.ingredientCount === 1 ? '1 ingrediente' : `${r.ingredientCount} ingredientes`);
  return parts.join(' · ');
}

type FrequencyFilter = 'all' | T.RecipeFrequency;
type CourseFilter = 'all' | T.RecipeCourse;

/** The household's recipe book; search and filters happen locally. */
function RecipesContent() {
  const navigate = useNavigate();
  const { data: recipes, error, loading, reload } = useLoad(() => api.getRecipes());
  const [search, setSearch] = useState('');
  const [frequency, setFrequency] = useState<FrequencyFilter>('all');
  const [course, setCourse] = useState<CourseFilter>('all');
  const [menu, setMenu] = useState<HTMLElement | null>(null);

  const visible = useMemo(() => {
    const key = searchKey(search);
    return (recipes ?? [])
      .filter(r => frequency === 'all' || r.frequency === frequency)
      .filter(r => course === 'all' || r.course === course)
      .filter(r => key.length === 0 || searchKey(r.name).includes(key));
  }, [recipes, search, frequency, course]);

  const all = recipes ?? [];
  const habitual = all.filter(r => r.frequency === 'Habitual').length;
  const countText = all.length === 0 ? '' : `${all.length} receitas · ${habitual} habituais · ${all.length - habitual} ocasionais`;

  return (
    <>
      <PageHeader
        eyebrow="Comida"
        title="Receitas"
        subtitle={countText || undefined}
        actions={<>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/casa/receitas/nova')}>Nova receita</Button>
          <IconButton aria-label="Atualizar" onClick={reload}><RefreshIcon /></IconButton>
          <IconButton aria-label="Mais opções" onClick={e => setMenu(e.currentTarget)}><MoreVertIcon /></IconButton>
        </>}
      />
      <Menu anchorEl={menu} open={menu !== null} onClose={() => setMenu(null)}>
        <MenuItem onClick={() => { setMenu(null); navigate('/casa/catalogo'); }}>
          <ListItemIcon><ListAltIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Catálogo de artigos" secondary="Os ingredientes e artigos de casa" />
        </MenuItem>
      </Menu>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Procurar receita"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ flexGrow: 1 }}
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
        />
        <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', minWidth: 0, flexShrink: 0, maxWidth: '100%' }}>
          <ToggleButtonGroup size="small" exclusive value={frequency} onChange={(_, v: FrequencyFilter | null) => v && setFrequency(v)} aria-label="Frequência">
            <ToggleButton value="all">Todas</ToggleButton>
            <ToggleButton value="Habitual">Habituais</ToggleButton>
            <ToggleButton value="Occasional">Ocasionais</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup size="small" exclusive value={course} onChange={(_, v: CourseFilter | null) => v && setCourse(v)} aria-label="Tipo">
            <ToggleButton value="all">Tudo</ToggleButton>
            <ToggleButton value="Main">Pratos</ToggleButton>
            <ToggleButton value="Starter">Entradas</ToggleButton>
            <ToggleButton value="Side">Acompanhamentos</ToggleButton>
            <ToggleButton value="Dessert">Sobremesas</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      <Loader loading={loading} error={error} onRetry={reload} hasData={recipes !== undefined}>
        {visible.length === 0 ? (
          <Paper variant="outlined">
            <EmptyState
              icon={<MenuBookOutlinedIcon />}
              title="Sem receitas para mostrar"
              text="Junta as que fazes com frequência: bastam o nome e os ingredientes. Os acompanhamentos (arroz, batata, salada) também são receitas."
              action={<Typography variant="caption" sx={{ fontStyle: 'italic' }}>«junta a receita de bacalhau à brás»</Typography>}
            />
          </Paper>
        ) : (
          <CardGrid min={260}>
            {visible.map(r => (
              <Card key={r.publicId} sx={{ height: '100%', '&:hover': { borderColor: 'primary.light', boxShadow: '0 6px 18px rgba(20, 35, 30, 0.08)' }, transition: 'box-shadow 120ms, border-color 120ms' }}>
                <CardActionArea onClick={() => navigate(`/casa/receitas/${r.publicId}`)} sx={{ height: '100%', p: 2, display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start' }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 2, flexShrink: 0, display: 'grid', placeItems: 'center', bgcolor: r.course === 'Side' ? 'rgba(217, 130, 43, 0.12)' : 'action.selected', color: r.course === 'Side' ? 'warning.dark' : 'primary.main' }}>
                      {r.course === 'Side' ? <RiceBowlOutlinedIcon fontSize="small" /> : <RestaurantIcon fontSize="small" />}
                    </Box>
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography sx={{ fontWeight: 650, lineHeight: 1.3, overflowWrap: 'anywhere' }}>{r.name}</Typography>
                      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>{cardSubtitle(r)}</Typography>
                    </Box>
                  </Stack>
                  {(r.tags.length > 0 || r.isQuick || r.yieldsLeftovers) && (
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1.25, flexWrap: 'wrap', rowGap: 0.5 }}>
                      {r.isQuick && <Chip size="small" icon={<BoltIcon />} label="rápida" color="primary" variant="outlined" />}
                      {r.yieldsLeftovers && <Chip size="small" label="dá sobras" variant="outlined" />}
                      {r.tags.map(tag => <Chip key={tag} size="small" label={tagText(tag)} variant="outlined" />)}
                    </Stack>
                  )}
                </CardActionArea>
              </Card>
            ))}
          </CardGrid>
        )}
      </Loader>
    </>
  );
}

const RecipesPage: React.FC = () => <NeedsHousehold><RecipesContent /></NeedsHousehold>;

export default RecipesPage;
